import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface GameSettings {
  musicVolume: number;        // 0-100
  soundVolume: number;        // 0-100 (placeholder for now)
  globalMute: boolean;        // Master mute toggle
  effectsEnabled: boolean;    // Effects and animations
  tooltipsEnabled: boolean;   // Popup tooltips
  actionHintsEnabled: boolean; // In-game action hints (wave signals)
  instructorEnabled: boolean;  // Descriptive guide (Instructor)
}

const DEFAULT_SETTINGS: GameSettings = {
  musicVolume: 30,
  soundVolume: 30,
  globalMute: false,
  effectsEnabled: true,
  tooltipsEnabled: true,
  actionHintsEnabled: true,
  instructorEnabled: true,
};

const STORAGE_KEY = 'opalstone_game_settings';

interface GameSettingsContextValue {
  settings: GameSettings;
  updateSetting: <K extends keyof GameSettings>(key: K, value: GameSettings[K]) => void;
  resetToDefaults: () => void;
}

const GameSettingsContext = createContext<GameSettingsContextValue | null>(null);

/**
 * Provider component that wraps the app and provides settings to all children
 */
export function GameSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<GameSettings>(() => {
    // Load from localStorage on initial mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load game settings from localStorage:', e);
    }
    return DEFAULT_SETTINGS;
  });

  // Persist to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save game settings to localStorage:', e);
    }
  }, [settings]);

  const updateSetting = useCallback(<K extends keyof GameSettings>(
    key: K,
    value: GameSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return (
    <GameSettingsContext.Provider value= {{ settings, updateSetting, resetToDefaults }
}>
  { children }
  </GameSettingsContext.Provider>
  );
}

/**
 * Hook for accessing game settings - must be used within GameSettingsProvider
 */
export function useGameSettings(): GameSettingsContextValue {
  const context = useContext(GameSettingsContext);
  if (!context) {
    // Fallback for components outside provider - use localStorage directly
    console.warn('[useGameSettings] Used outside GameSettingsProvider, settings may not sync');

    // Return a fallback that reads from localStorage
    let settings = DEFAULT_SETTINGS;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        settings = { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (e) { }

    return {
      settings,
      updateSetting: () => { },
      resetToDefaults: () => { },
    };
  }
  return context;
}

export default useGameSettings;
